import {
  transformCta,
  flattenCustomImage,
  toRelativeUrl,
} from '../../utils/helperTransforms.wp';

import {
  placeholderImage,
  placeholderCta,
  placeholderCard,
  placeholderCardHero,
} from '../../utils/helperPlaceholder';

// The News and Event card types each imply their own post type and carry their
// own ACF field group (`news` / `events`) holding the query options. Everything
// else is either custom cards or the generic `pages` source.
const POST_CARD_TYPES = {
  news: {
    postType: 'news',
    group: 'news',
    taxonomyField: 'newsTaxonomyPrimary',
  },
  event: {
    postType: 'events',
    group: 'events',
    taxonomyField: 'eventsTaxonomyPrimary',
  },
};

// Post types whose carousels are always rendered on the dynamic colour band.
// Shared with CardCarousel.jsx (heading placement) and BlockLayout's
// normaliseBlocks (colour demotion opt-out) so the three stay in lockstep.
export const COLOURED_SOURCE_POST_TYPES = ['news', 'events'];

export const isPostCarousel = (block) =>
  block?.type === 'card-carousel' &&
  COLOURED_SOURCE_POST_TYPES.includes(block?.sourcePostType);

// Selected posts come back as a WPGraphQL connection; the query only needs IDs.
function selectedPostIds(group) {
  return group?.selectedPosts?.edges?.map(({ node }) => node?.databaseId) || [];
}

export function transformCardCarousel(data) {
  //   console.dir(data.news);
  const cardType = data?.cardType;
  const postCardType = POST_CARD_TYPES[cardType];

  const source =
    cardType == 'content'
      ? 'custom'
      : postCardType
        ? 'postType'
        : data?.contentSource;
  const sourcePostType = postCardType
    ? postCardType.postType
    : source === 'postType'
      ? Array.isArray(data?.sourcePostType)
        ? data.sourcePostType[0]
        : data?.sourcePostType || 'page'
      : 'page';

  // News and event carousels always sit on the dynamic colour band, matching the
  // related-stories carousel rendered under news articles in BlockLayout.
  const coloured = COLOURED_SOURCE_POST_TYPES.includes(sourcePostType);

  return {
    type: 'card-carousel',
    blockId: data?.addToAnchor ? data?.anchorId || false : false,
    blockTitle: data?.addToAnchor ? data?.anchorTitle || false : false,
    theme: coloured ? 'dynamic' : false,
    colorBackground: coloured,

    animateHeading: data?.headingType == 'animate' || false,
    heading: data?.heading || false,
    subheading: data?.subheading || false,

    cta: transformCta(data?.cta),
    // slidesPerView: parseInt(data?.slidesPerView, 10) || false,
    slidesPerView: 3,
    cardType: cardType || false,
    source: source || false,
    sourcePostType: sourcePostType,
    customTemplate: data?.customTemplate || 'default',
    ...(source === 'postType'
      ? postCardType
        ? (() => {
            const group = data?.[postCardType.group];
            const taxonomy = group?.[postCardType.taxonomyField];

            return {
              filter: group?.filter || false,
              selectedPosts: selectedPostIds(group),
              taxonomy: Array.isArray(taxonomy)
                ? taxonomy[0]
                : taxonomy || false,
            };
          })()
        : {
            filter: data?.pages?.filter || false,
            selectedPosts: selectedPostIds(data?.pages),
          }
      : {}),
    cards:
      source === 'custom'
        ? (data?.cards || []).map((card) => ({
            heading: card?.heading || false,
            tags: card?.subheading ? [{ name: card?.subheading }] : [],
            description: card?.description || false,
            image: flattenCustomImage(card?.image),
            link: card?.link
              ? {
                  target: card?.link?.target || false,
                  title: card?.link?.title || false,
                  url: toRelativeUrl(card?.link?.url || false),
                }
              : false,
          }))
        : [],
  };
}

export const DEMO_DATA = [
  {
    type: 'card-carousel',
    cardType: 'hero',
    theme: 'dynamic',
    // colorBackground: true,
    subheading: 'Beyond the classroom',
    heading: 'Beyond the classroom',
    cards: [placeholderCard, placeholderCard, placeholderCard],
  },
  {
    type: 'card-carousel',
    cardType: 'hero',
    theme: 'dynamic',
    // colorBackground: true,
    heading: 'Pathways',
    animateHeading: true,
    cards: [placeholderCard, placeholderCard, placeholderCard, placeholderCard],
  },
  {
    type: 'card-carousel',
    cardType: 'hero',
    theme: 'dynamic',
    // colorBackground: true,
    cards: [
      {
        ...placeholderCard,
      },
      {
        ...placeholderCard,
      },
      placeholderCard,
      placeholderCard,
      placeholderCard,
      placeholderCard,
      placeholderCard,
      placeholderCard,
      placeholderCard,
    ],
  },
  {
    // A non-hero set of 3: a carousel below 1024, a static row above it.
    type: 'card-carousel',
    cardType: 'high-content',
    theme: 'dynamic',
    heading: 'Three across',
    cards: [placeholderCard, placeholderCard, placeholderCard],
  },
  {
    type: 'card-carousel',
    cardType: 'news',
    theme: 'dynamic',
    colorBackground: true,
    cards: [
      {
        ...placeholderCard,
      },
      {
        ...placeholderCard,
      },
      placeholderCard,
      placeholderCard,
      placeholderCard,
      placeholderCard,
    ],
  },
  {
    type: 'card-carousel',
    cardType: 'event',
    sourcePostType: 'events',
    theme: 'dynamic',
    colorBackground: true,
    heading: 'Upcoming events',
    cards: [
      { ...placeholderCard, eventDate: '12 December', eventTime: '8am - 4pm' },
      { ...placeholderCard, eventDate: '2 February', eventTime: '6pm - 9pm' },
      // Both fields are free text on the events CPT, so either can be missing.
      { ...placeholderCard, eventDate: '14 March' },
      { ...placeholderCard, eventDate: '9 April', eventTime: '10am - 2pm' },
    ],
  },
];
